import { Box, Button, Stack } from '@chakra-ui/react';
import { DateValue } from '@internationalized/date';
import { Show } from '@pelicargo/ui';
import { formatAnyDateTypeToZonedDateTime, TimeZone } from '@pelicargo/utils';
import { DateInputProps, DatePickerCalendar, DatePickerStatic, DatePickerTimeField } from '@saas-ui/date-picker';
import { FocusEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { FieldPathByValue, useFormContext, useWatch } from 'react-hook-form';

import { SimpleField, SimpleXProps } from './SimpleField';

type SimpleDatePickerProps<FormValues> = SimpleXProps &
  Omit<DateInputProps, 'name' | 'defaultValue'> & {
    initialTimeZone?: TimeZone;
    name: FieldPathByValue<FormValues, string | Date>;
    showTime?: boolean;
    defaultValue?: Date;
    showClearButton?: boolean;
  };

export const SimpleDatePicker = <FormValues,>({
  name,
  label,
  helper,
  initialTimeZone = 'America/New_York',
  showTime = true,
  isDisabled,
  defaultValue,
  showClearButton = false,
  ...rest
}: SimpleDatePickerProps<FormValues>) => {
  const { setValue } = useFormContext<FormValues>();
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const timeFieldContainerRef = useRef<HTMLDivElement>(null);

  const formattedDefaultValue = useMemo(() => {
    if (!defaultValue) return null;

    return formatAnyDateTypeToZonedDateTime(defaultValue, initialTimeZone);
  }, [defaultValue, initialTimeZone]);

  const formValue = useWatch<FormValues>({
    name,
    defaultValue: formattedDefaultValue as any,
  });

  const handleDateChange = useCallback(
    (nextDate: DateValue | null) => {
      const zonedDate = formatAnyDateTypeToZonedDateTime(nextDate, initialTimeZone);
      const nativeDate = zonedDate?.toDate('');

      setValue(name, nativeDate as any, { shouldDirty: true });
    },
    [name, setValue, initialTimeZone],
  );

  const value = useMemo(() => {
    const nextValue = (formValue as Date) || defaultValue;
    if (!nextValue) return null;

    return formatAnyDateTypeToZonedDateTime(nextValue, initialTimeZone);
  }, [defaultValue, formValue, initialTimeZone]);

  const calendarStyles = useMemo(
    () => ({
      table: { width: '100%' },
      'td button[tabindex="0"]': {
        boxShadow: '0 0 0 3px var(--chakra-colors-primary-500)',
        borderRadius: 'md',
      },
      'td button[tabindex="0"][data-selected]': {
        boxShadow: '0 0 0 3px var(--chakra-colors-primary-500)',
      },
      'td button[tabindex="0"][data-today]:not([data-selected])': {
        bg: 'blackAlpha.100',
        _dark: {
          bg: 'whiteAlpha.100',
        },
      },
    }),
    [],
  );

  const timeFieldStyles = useMemo(
    () => ({
      '[role="group"]:focus-within': {
        borderColor: 'primary.500',
        boxShadow: '0 0 0 1px var(--chakra-colors-primary-500)',
      },
      '[role="spinbutton"]:focus, [role="spinbutton"]:focus-visible': {
        boxShadow: '0 0 0 2px var(--chakra-colors-primary-500)',
        borderRadius: 'sm',
      },
    }),
    [],
  );

  const handleTimeFieldTabOrder = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Tab' || event.shiftKey || isDisabled) return;

      const timeFieldRoot = event.currentTarget as HTMLElement;
      const segments = Array.from(timeFieldRoot.querySelectorAll<HTMLElement>('[role="spinbutton"]'));
      const [hourSegment, minuteSegment] = segments;

      const currentTarget = (event.target as HTMLElement | null)?.closest('[role="spinbutton"]') as HTMLElement | null;
      if (!currentTarget || !hourSegment || !minuteSegment) return;
      if (currentTarget !== hourSegment) return;

      if (!minuteSegment) return;

      event.preventDefault();
      minuteSegment.focus();
    },
    [isDisabled],
  );

  const handleCalendarFocus = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      if (isDisabled) return;

      const currentTarget = event.target as HTMLElement | null;
      if (!currentTarget?.matches('[role="application"]')) return;

      const activeDayButton = calendarContainerRef.current?.querySelector<HTMLElement>(
        'td button[tabindex="0"]:not([aria-disabled="true"])',
      );

      activeDayButton?.focus();
    },
    [isDisabled],
  );

  const handleTimeFieldFocus = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      if (isDisabled) return;

      const currentTarget = event.target as HTMLElement | null;
      if (!currentTarget?.matches('[role="group"]')) return;

      const hourSegment = timeFieldContainerRef.current?.querySelector<HTMLElement>('[role="spinbutton"]');
      hourSegment?.focus();
    },
    [isDisabled],
  );

  useEffect(() => {
    const calendarRoot = calendarContainerRef.current;
    if (!calendarRoot) return;

    const navigationButtons = calendarRoot.querySelectorAll<HTMLButtonElement>(
      '[aria-label="Previous"], [aria-label="Next"]',
    );
    navigationButtons.forEach((button) => {
      if (button.tabIndex !== -1) {
        button.tabIndex = -1;
      }
    });
  });

  return (
    <SimpleField<DateInputProps> name={name} label={label} helper={helper} isDisabled={isDisabled} {...rest}>
      <Stack spacing="1">
        <DatePickerStatic
          hourCycle={24}
          granularity="minute"
          value={value as any}
          onChange={handleDateChange}
          isDisabled={isDisabled}
          timeZone={initialTimeZone}
        >
          <Box ref={calendarContainerRef} onFocusCapture={handleCalendarFocus}>
            <DatePickerCalendar w="full" opacity={isDisabled ? 0.4 : 1} sx={calendarStyles} />
          </Box>
          <Show if={showTime}>
            <Box
              ref={timeFieldContainerRef}
              onFocusCapture={handleTimeFieldFocus}
              onKeyDownCapture={handleTimeFieldTabOrder}
              sx={timeFieldStyles}
            >
              <DatePickerTimeField isDisabled={isDisabled} />
            </Box>
          </Show>
        </DatePickerStatic>
        <Stack align="end">
          <Show if={showClearButton}>
            <Button w="auto" onClick={() => setValue(name, null)}>
              Clear Date
            </Button>
          </Show>
        </Stack>
      </Stack>
    </SimpleField>
  );
};
